<template>
  <PageChrome>
    <template #title>
      <span class="eyebrow">Sequence</span>
    </template>

    <template #actions>
      <router-link :to="backTo" class="backLink">
        <span class="backArrow">&larr;</span> Back to play
      </router-link>
    </template>

    <div class="sequencePage">
      <div v-if="loadingPlay" class="pageLoading">Loading&hellip;</div>
      <div v-else-if="playError" class="error">{{ playError }}</div>
      <div v-else-if="!resolvedSequenceId" class="noSequence">
        No sequence configured for this play.
        <router-link :to="backTo" class="backLink">Return to play</router-link>
      </div>

      <div v-else-if="loadingSeq" class="pageLoading">Loading&hellip;</div>
      <div v-else-if="seqError" class="error">{{ seqError }}</div>

      <template v-else-if="sequence">
        <!-- Header -->
        <div class="seqHeader">
          <div class="seqTitleRow">
            <h1 class="seqName">{{ sequence.name }}</h1>
            <span class="versionBadge">v{{ sequence.current_version }}</span>
            <span v-if="sequence.has_enrollments" class="lockedBadge" title="Steps are locked — contacts have been enrolled">
              <AppIcon name="agent_guardrail" :size="12" /> Locked
            </span>
          </div>
          <p v-if="sequence.description" class="seqDesc">{{ sequence.description }}</p>
        </div>

        <!-- Stats bar -->
        <SequenceStatsBar
          class="statsBarRow"
          :activeCount="statsData.active"
          :engagedCount="statsData.engaged"
          :timedOutCount="statsData.timed_out"
          :disqualifiedCount="statsData.disqualified"
          :activeFilter="statsBarFilter"
          @filter="onStatsBarFilter"
        />

        <!-- Step funnel — clickable -->
        <div class="funnelSection">
          <div class="sectionLabel">
            Step Funnel
            <span class="sectionHint">Click a step or exit to filter contacts below</span>
          </div>
          <SequenceSummary
            :steps="sequence.steps"
            :stepCounts="stepCountsFlat"
            :stepCountsReady="stepReadyCountsFlat"
            :pendingExit="sequenceStats?.pending_exit_count || 0"
            :exitedCompleted="statsData.engaged"
            :exitedTimedOut="statsData.timed_out"
            :exitedDisqualified="statsData.disqualified"
            :clickable="true"
            :activeStepIndex="activeStepFilter?.type === 'step' ? activeStepFilter.index : null"
            :activeExitType="activeStepFilter?.type === 'exit' ? activeStepFilter.exitType : null"
            @step-click="onStepClick"
            @exit-click="onExitClick"
          />
        </div>

        <!-- Contacts table -->
        <div class="contactsSection">
          <div class="sectionLabel">Contacts</div>
          <SequenceContactsTable
            :sequenceId="String(sequence.id)"
            :steps="sequence.steps"
            :stepFilter="activeStepFilter"
            :participantAccounts="participantAccounts"
            :pollTick="pollTick"
            @total="onTotal"
            @clear-filter="clearStepFilter"
            @reachout-click="openReachout"
          />
        </div>

        <!-- Email delivery stats -->
        <div class="emailSection">
          <div class="sectionLabel">Email Delivery</div>
          <SequenceEmailStats :sequenceId="String(sequence.id)" :pollTick="pollTick" />
        </div>
      </template>
    </div>
  </PageChrome>

  <!-- Reachout modal -->
  <ReachoutModal
    v-if="reachoutData"
    :reachout="reachoutData"
    @close="reachoutData = null"
  />
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import AppIcon from '../../components/AppIcon.vue'
import SequenceStatsBar from '../../components/SequenceStatsBar.vue'
import SequenceSummary from '../../components/SequenceSummary.vue'
import SequenceContactsTable from '../../components/SequenceContactsTable.vue'
import SequenceEmailStats from '../../components/SequenceEmailStats.vue'
import ReachoutModal from '../../components/ReachoutModal.vue'
import { apiFetch } from '../../services/api.ts'
import { useChatScope } from '../composables/useChatScope'

const route = useRoute()
const { setScope, clearScope } = useChatScope()

const playId = computed(() => String(route.params.id || ''))
const backTo = computed(() => `/sales-plays/${playId.value}`)

// Play fetch to resolve sequence ID
const loadingPlay = ref(false)
const playError = ref('')
const resolvedSequenceId = ref(null)

const sequence = ref(null)
const loadingSeq = ref(false)
const seqError = ref('')
const sequenceStats = ref(null)
const participantAccounts = ref([])

// Polling — refresh stats every 30s so bounces / replies / step changes appear
const POLL_INTERVAL_MS = 30_000
const pollTick = ref(0)
let pollTimer = null

// Reachout modal
const reachoutData = ref(null)

async function openReachout(reachoutId) {
  try {
    const r = await apiFetch(`/reachouts/${encodeURIComponent(reachoutId)}`)
    reachoutData.value = r
  } catch {
    // ignore — user can try again
  }
}

// Step/exit filter driven by funnel clicks
// { type: 'step', index: N } | { type: 'exit', exitType: 'completed'|'timed_out'|'removed' } | null
const activeStepFilter = ref(null)

// Stats bar filter (separate from funnel — drives SequenceStatsBar highlight only)
const statsBarFilter = computed(() => {
  const f = activeStepFilter.value
  if (!f) return null
  if (f.type === 'active') return 'active'
  if (f.type === 'step') return 'active'
  if (f.type === 'exit') {
    if (f.exitType === 'completed') return 'engaged'
    if (f.exitType === 'timed_out') return 'timed_out'
    if (f.exitType === 'removed') return 'disqualified'
  }
  return null
})

function onStepClick(stepIndex) {
  // Toggle: clicking the same step again clears the filter
  if (activeStepFilter.value?.type === 'step' && activeStepFilter.value.index === stepIndex) {
    activeStepFilter.value = null
  } else {
    activeStepFilter.value = { type: 'step', index: stepIndex }
  }
}

function onExitClick(exitType) {
  if (activeStepFilter.value?.type === 'exit' && activeStepFilter.value.exitType === exitType) {
    activeStepFilter.value = null
  } else {
    activeStepFilter.value = { type: 'exit', exitType }
  }
}

function onStatsBarFilter(key) {
  // Stats bar clicks map to exit filters
  if (!key) {
    activeStepFilter.value = null
    return
  }
  const exitMap = { engaged: 'completed', timed_out: 'timed_out', disqualified: 'removed' }
  if (exitMap[key]) {
    onExitClick(exitMap[key])
  } else if (key === 'active' || key === 'active,paused') {
    if (activeStepFilter.value?.type === 'active') {
      activeStepFilter.value = null
    } else {
      activeStepFilter.value = { type: 'active' }
    }
  }
}

function clearStepFilter() {
  activeStepFilter.value = null
}

function onTotal(n) { /* could update breadcrumb */ }

const statsData = computed(() => {
  const s = sequenceStats.value
  if (!s) return { active: 0, completed: 0, engaged: 0, timed_out: 0, disqualified: 0 }
  return {
    active: sequence.value?.active_enrollment_count || 0,
    completed: s.exited_completed || 0,
    engaged: s.exited_completed || 0,
    timed_out: s.exited_timed_out || 0,
    disqualified: s.exited_removed || 0,
  }
})

const stepCountsFlat = computed(() => {
  const flat = []
  for (const ss of sequenceStats.value?.step_stats || []) {
    flat[ss.step_index] = ss.count
  }
  return flat
})

const stepReadyCountsFlat = computed(() => {
  const flat = []
  for (const ss of sequenceStats.value?.step_stats || []) {
    flat[ss.step_index] = ss.ready_count
  }
  return flat
})

async function fetchPlay() {
  loadingPlay.value = true
  playError.value = ''
  try {
    const resp = await apiFetch(`/sales-plays/${encodeURIComponent(playId.value)}`)
    const play = resp?.play || resp
    const sequenceAction = (play?.actions || []).find((a) => a.action_type === 'sequence')
    const contactSequenceId = sequenceAction?.definition?.contact_sequence_id
    resolvedSequenceId.value = contactSequenceId ? String(contactSequenceId) : null
  } catch (e) {
    playError.value = e?.message || 'Failed to load play'
  } finally {
    loadingPlay.value = false
  }
}

async function loadSequence() {
  if (!resolvedSequenceId.value) return
  loadingSeq.value = true
  seqError.value = ''
  try {
    const [seqResp, statsResp, accountsResp] = await Promise.all([
      apiFetch(`/sequences/${encodeURIComponent(resolvedSequenceId.value)}`),
      apiFetch('/sequences/stats').catch(() => ({ stats: [] })),
      apiFetch(`/sequences/${encodeURIComponent(resolvedSequenceId.value)}/participant-accounts`).catch(() => []),
    ])
    sequence.value = seqResp
    const allStats = statsResp.stats || []
    sequenceStats.value = allStats.find(s => String(s.sequence_id) === String(resolvedSequenceId.value)) || null
    participantAccounts.value = Array.isArray(accountsResp) ? accountsResp : []
  } catch (e) {
    seqError.value = e?.message || 'Failed to load sequence'
  } finally {
    loadingSeq.value = false
  }
}

// Silent background refresh — no loading spinners, no error overwrite
async function pollRefresh() {
  if (!resolvedSequenceId.value) return
  try {
    const [seqResp, statsResp] = await Promise.all([
      apiFetch(`/sequences/${encodeURIComponent(resolvedSequenceId.value)}`),
      apiFetch('/sequences/stats').catch(() => ({ stats: [] })),
    ])
    sequence.value = seqResp
    const allStats = statsResp.stats || []
    sequenceStats.value = allStats.find(s => String(s.sequence_id) === String(resolvedSequenceId.value)) || null
    pollTick.value++
  } catch {
    // silent — don't disrupt the page on a transient poll failure
  }
}

onMounted(async () => {
  setScope({
    scopeType: 'workspace',
    scopeSubjectId: '',
    pageContext: {
      page_type: 'sequence',
      sequence_id: String(route.params.sequenceId || route.params.id || ''),
    },
  })
  await fetchPlay()
  await loadSequence()
  pollTimer = setInterval(pollRefresh, POLL_INTERVAL_MS)
})

onBeforeUnmount(() => {
  clearScope()
  if (pollTimer) clearInterval(pollTimer)
})
</script>

<style scoped>
.sequencePage {
  --border: var(--color-border-subtle);
  --border2: var(--color-border-subtle);
  --surface: var(--color-bg-surface);
  --text: var(--color-text-primary);
  --muted: var(--color-text-tertiary);
  --primary: var(--color-accent);
  --success: var(--color-success);
  --danger: var(--color-danger);
  --badge-bg: var(--color-bg-subtle);
  --row-hover-bg: var(--color-bg-subtle);
  --hover-bg: var(--color-bg-subtle);
  --link: var(--color-accent);
  --panel: var(--color-bg-surface);
  --field-bg: var(--color-bg-subtle);
  --table-header-bg: var(--color-bg-subtle);
  --focus-border: var(--color-accent);
  font-family: var(--font-sans);
}

.eyebrow {
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-tertiary);
}

.backLink {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2, 6px);
  padding: var(--space-2, 6px) var(--space-3, 10px);
  background: transparent;
  border: 0;
  border-radius: var(--radius-md, 6px);
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--fs-sm, 13px);
  font-weight: var(--fw-semibold, 600);
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: color var(--duration-fast, 100ms) var(--ease-out, ease-out);
}
.backLink:hover { color: var(--color-accent); }
.backArrow { display: inline-block; }

.noSequence {
  margin-top: 40px;
  font-size: 14px;
  color: var(--muted);
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
}

.pageLoading { color: var(--muted); font-size: 14px; }

.seqHeader { margin-bottom: 20px; }

.seqTitleRow {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.seqName {
  font-size: 22px;
  font-weight: 700;
  color: var(--text);
  margin: 0;
}

.versionBadge {
  font-size: 12px;
  font-weight: 600;
  color: var(--muted);
  background: var(--surface2, var(--surface));
  border: 1px solid var(--border);
  border-radius: 5px;
  padding: 2px 7px;
}

.lockedBadge {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: var(--warning, #f59e0b);
  background: color-mix(in srgb, var(--warning, #f59e0b) 10%, var(--surface));
  border: 1px solid color-mix(in srgb, var(--warning, #f59e0b) 30%, var(--border));
  border-radius: 5px;
  padding: 2px 7px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.seqDesc {
  margin: 6px 0 0;
  font-size: 14px;
  color: var(--muted);
  line-height: 1.4;
}

.statsBarRow { margin-bottom: 24px; }

.funnelSection { margin-bottom: 28px; }

.emailSection { margin-top: 32px; }

.sectionLabel {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--muted);
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.sectionHint {
  font-size: 11px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: var(--muted);
  opacity: 0.7;
}
</style>
